import { useEffect, useRef, useState } from 'react';

import { useCityStore } from '@/stores/city/useCityStore';

import { ProductShelfTab, ProductShelfTabResult } from '../models';

import { getFilteredProductsService } from '@/client/services/vendor/methods';
import { OfferContent } from '@/server/services/vendor/models';

const FETCH_SIZE = 14;
const MAX_VISIBLE = 10;
const HIDDEN_DISCOUNT_PERCENTAGE = 99;
/** Start fetching a little before the shelf scrolls into view so cards are ready on arrival. */
const PREFETCH_MARGIN = '600px 0px';

const isPurchasable = (offer: OfferContent) =>
    offer.offerId != null && (offer.availableQuantity ?? 0) > 0 && offer.price != null && offer.discountPercentage !== HIDDEN_DISCOUNT_PERCENTAGE;

/**
 * Lazily loads a tab’s offers from lowest to highest price once the shelf is near the viewport.
 * Results are cached per city + tab, so switching tabs back and forth never refetches.
 */
export const useShelfProducts = (activeTab: ProductShelfTab) => {
    const cityId = useCityStore((state) => state.cityId);
    const sectionRef = useRef<HTMLElement>(null);
    const requested = useRef(new Set<string>());
    const [inView, setInView] = useState(false);
    const [results, setResults] = useState<Record<string, ProductShelfTabResult>>({});
    const [retry, setRetry] = useState(0);
    const resultKey = `${cityId}:${activeTab.id}:${retry}`;

    useEffect(() => {
        const node = sectionRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setInView(true);
                    observer.disconnect();
                }
            },
            { rootMargin: PREFETCH_MARGIN }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!inView || requested.current.has(resultKey)) return;
        requested.current.add(resultKey);
        const load = async () => {
            try {
                const response = await getFilteredProductsService({
                    categoryIds: [activeTab.categoryId],
                    cityId,
                    page: 0,
                    size: FETCH_SIZE,
                    sortBy: 'price_asc'
                });
                const content = Array.isArray(response.data.content) ? response.data.content : [];
                setResults((current) => ({
                    ...current,
                    [resultKey]: {
                        items: content.filter(isPurchasable).slice(0, MAX_VISIBLE),
                        total: response.data.totalElements ?? content.length,
                        error: false
                    }
                }));
            } catch {
                setResults((current) => ({ ...current, [resultKey]: { items: [], total: 0, error: true } }));
            }
        };
        void load();
    }, [inView, resultKey, activeTab.categoryId, cityId]);

    return { sectionRef, resultKey, result: results[resultKey], inView, retry: () => setRetry((value) => value + 1) };
};
